/**
 * マイページ = 生徒ビューの学習レポート(docs/07_learning_support.md §4.2)。
 *
 * ┌────────────────────────────────────────────┐
 * │ ✨ できるようになったこと(★が増えた / はやさの伸び) │  ← 先頭は必ず「できたこと」
 * │ 🔥 がんばり(れんぞく日数・そうタイプすう・じかん)     │
 * │ ⌨️ これから のびるキー(TOP5 + ヒートマップ)         │
 * │ あ  これから のびる かな(TOP5)                    │
 * │ [ のびるキーだけ れんしゅう ]                       │
 * └────────────────────────────────────────────┘
 *
 * 表現はすべてポジティブ側(「にがて」ではなく「これから のびる」)。
 * データが無いときは数字を出さず励ましだけを出す。
 */

import { useMemo } from 'react';
import { bindingForChar } from '../logic/keyboard';
import {
  keyDisplayLabel,
  keyHeatByKeyId,
  rankWeakSkills,
  MIN_ATTEMPTS,
  type WeakSkill,
} from '../logic/weakKeys';
import { currentStreak, learningRecorder, selectAchievements } from '../services/learning';
import { useGameStore } from '../store/gameStore';
import { HEAT_LEGEND, KeyboardHeatmap } from './components/KeyboardHeatmap';
import { BirthdaySettings } from './BirthdaySettings';
import { LanguageSettings } from './LanguageSettings';
import { NameSettings } from './NameSettings';
import { PalColorSettings } from './PalColorSettings';
import { PalHelpSettings } from './PalHelpSettings';
import './mypage.css';

const DIFFICULTY_LABELS: Readonly<Record<string, string>> = {
  easy: 'かんたん',
  normal: 'ふつう',
  hard: 'むずかしい',
};

const TOP_N = 5;

/** 1分に満たないうちは「0ふん」ではなく秒で見せる(最初のプレイでも数字が動くように)。 */
function playTime(durationMs: number): { readonly value: number; readonly unit: string } {
  return durationMs < 60_000
    ? { value: Math.round(durationMs / 1000), unit: 'びょう' }
    : { value: Math.round(durationMs / 60_000), unit: 'ふん' };
}

function percent(rate: number): number {
  return Math.round(rate * 100);
}

/** 打てた割合(ミス率の裏返し)。ポジティブ表現のためこちらを主役にする。 */
function hitPercent(skill: WeakSkill): number {
  return Math.max(0, 100 - percent(skill.missRate));
}

export function MyPageScreen() {
  const backFromMeta = useGameStore((state) => state.backFromMeta);
  const startWeakKeyPractice = useGameStore((state) => state.startWeakKeyPractice);

  // 画面に入るたびに読み直す(プレイ直後の結果がそのまま反映される)。
  const snapshot = useMemo(() => learningRecorder.load(), []);
  const achievements = useMemo(() => selectAchievements(snapshot), [snapshot]);
  const streak = useMemo(() => currentStreak(snapshot.days), [snapshot]);
  const weakKeys = useMemo(() => rankWeakSkills(snapshot.keys, { limit: TOP_N }), [snapshot]);
  const weakKana = useMemo(() => rankWeakSkills(snapshot.kana, { limit: TOP_N }), [snapshot]);
  const heat = useMemo(() => keyHeatByKeyId(snapshot.keys), [snapshot]);
  const highlight = useMemo(() => {
    const ids = new Set<string>();
    for (const skill of weakKeys) {
      const binding = bindingForChar(skill.id);
      if (binding) ids.add(binding.keyId);
    }
    return ids;
  }, [weakKeys]);

  const played = snapshot.totals.sessions > 0;
  const hasAchievement = achievements.starGains.length > 0 || achievements.speed !== null;

  return (
    <div className="screen my-page">
      <header className="my-page-header">
        <button type="button" className="back-button" onClick={backFromMeta}>
          もどる
        </button>
        <h1 className="screen-title">きみの きろく</h1>
        <span className="my-page-spacer" />
      </header>

      {/* ── できるようになったこと(先頭)── */}
      <section className="card can-do">
        <h2 className="card-title">✨ できるように なったこと</h2>
        {hasAchievement ? (
          <ul className="can-do-list">
            {achievements.starGains.map((gain) => (
              <li key={`${gain.stageId}-${gain.difficulty}-${gain.at}`}>
                <span className="badge-star">★{gain.stars}</span>「{gain.stageTitle}」(
                {DIFFICULTY_LABELS[gain.difficulty] ?? gain.difficulty})で ★が{' '}
                {gain.stars - gain.previousStars} こ ふえた!
              </li>
            ))}
            {achievements.speed ? (
              <li>
                <span className="badge-speed">はやさ</span>
                {achievements.speed.deltaKpm > 0
                  ? `まえより ${achievements.speed.deltaKpm} だ/ぷん はやく なったよ!(いま ${achievements.speed.recentKpm} だ/ぷん)`
                  : `いまの はやさは ${achievements.speed.recentKpm} だ/ぷん。じぶんの さいこうは ${achievements.bestKpm} だ/ぷんだよ!`}
              </li>
            ) : null}
          </ul>
        ) : (
          <p className="empty-text">
            {played
              ? 'きろくを ためて いるところ! もう 1かい あそぶと、できるように なったことが ここに でるよ。'
              : 'まだ あそんで いないね。1かい あそぶだけで、ここに きみの できたことが たまっていくよ!'}
          </p>
        )}
      </section>

      {/* ── がんばり ── */}
      <section className="card effort">
        <h2 className="card-title">🔥 がんばり</h2>
        <dl className="effort-grid">
          <div className="effort-item">
            <dt>れんぞく</dt>
            <dd>
              {streak > 0 ? `🔥 ${streak}` : '0'} <span className="unit">にち</span>
            </dd>
          </div>
          <div className="effort-item">
            <dt>ぜんぶで うった かず</dt>
            <dd>
              {snapshot.totals.typed} <span className="unit">だ</span>
            </dd>
          </div>
          <div className="effort-item">
            <dt>あそんだ じかん</dt>
            <dd>
              {playTime(snapshot.totals.durationMs).value}{' '}
              <span className="unit">{playTime(snapshot.totals.durationMs).unit}</span>
            </dd>
          </div>
          <div className="effort-item">
            <dt>あそんだ かいすう</dt>
            <dd>
              {snapshot.totals.sessions} <span className="unit">かい</span>
            </dd>
          </div>
        </dl>
        {streak >= 2 ? <p className="cheer">{streak}にち つづいてる! すごい!</p> : null}
      </section>

      {/* ── これから のびるキー ── */}
      <section className="card grow-keys">
        <h2 className="card-title">⌨️ これから のびる キー</h2>
        {weakKeys.length > 0 ? (
          <>
            <p className="card-lead">この キーを れんしゅうすると、もっと はやく なれるよ!</p>
            <ol className="grow-list">
              {weakKeys.map((skill, index) => (
                <li key={skill.id}>
                  <span className="rank">{index + 1}</span>
                  <span className="grow-key">{keyDisplayLabel(skill.id)}</span>
                  <span className="grow-detail">
                    {hitPercent(skill)}% うてた({skill.attempts}かい ちょうせん)
                    {skill.avgReactionMs !== null ? ` / ${skill.avgReactionMs}ミリびょう` : ''}
                  </span>
                </li>
              ))}
            </ol>
          </>
        ) : (
          <p className="empty-text">
            まだ データが すくないよ。おなじ キーを {MIN_ATTEMPTS}かい いじょう うつと、 ここで
            しらべられるように なるよ!
          </p>
        )}

        <KeyboardHeatmap heat={heat} highlight={highlight} />
        <ul className="heat-legend">
          {HEAT_LEGEND.map((item) => (
            <li key={item.label}>
              <span className="swatch" style={{ background: item.color }} /> {item.label}
            </li>
          ))}
        </ul>

        <button
          type="button"
          className="big-button practice"
          disabled={weakKeys.length === 0}
          onClick={() => void startWeakKeyPractice(weakKeys.map((skill) => skill.id))}
        >
          のびるキーだけ れんしゅう!
        </button>
        <p className="practice-note">れんしゅうモードは きろく(★・コイン)に ひびかないよ。</p>
      </section>

      {/* ── これから のびる かな ── */}
      <section className="card grow-kana">
        <h2 className="card-title">あ これから のびる かな</h2>
        {weakKana.length > 0 ? (
          <ol className="grow-list kana">
            {weakKana.map((skill, index) => (
              <li key={skill.id}>
                <span className="rank">{index + 1}</span>
                <span className="grow-key">{skill.id}</span>
                <span className="grow-detail">
                  {hitPercent(skill)}% うてた({skill.attempts}かい ちょうせん)
                </span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="empty-text">
            かなの データは これからだよ。にほんごの ステージで あそぶと たまっていくよ!
          </p>
        )}
      </section>

      {/* ── なまえ(docs/12 §1。ランキング・リーグに出る表示名)── */}
      <NameSettings />

      {/* ── パルの いろ(docs/14 §2。既定オフ = 白いパル)── */}
      <PalColorSettings />

      {/* ── パルの おてつだい(docs/07 §7.4。既定オン = ヒントを出す)── */}
      <PalHelpSettings />

      {/* ── ことばの せってい(docs/10 §1 海外展開の土台)── */}
      <LanguageSettings />

      {/* ── おたんじょうび(docs/12 §1 のランキング年齢別。任意)── */}
      <BirthdaySettings />
    </div>
  );
}
